import React from 'react';
import { Users, Target, Globe, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import PageTitle from '../hooks/PageTitle';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="About Us" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              About Ashbourne Law
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A premier international law firm delivering sophisticated legal solutions with precision and excellence
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-serif text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded in London, Ashbourne Law has grown into a distinguished international law firm with a reputation for excellence in corporate, commercial, and cross-border legal matters.
                </p>
                <p>
                  Our firm was established on the principle that sophisticated legal counsel should combine deep technical expertise with genuine business acumen. Today, we serve corporations, financial institutions, and high net worth individuals across multiple jurisdictions.
                </p>
                <p>
                  With our London Global Headquarters and developing hubs in Luxembourg and Dubai, we provide seamless legal support for complex international transactions while maintaining the personalized service of a boutique practice.
                </p>
              </div>
            </div>
            <div className="relative h-96 lg:h-full min-h-[400px]">
              <img
                src="https://images.unsplash.com/photo-1497366811353-6870744d04b2"
                alt="Modern office"
                className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-0 shadow-md">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-3">Excellence</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Delivering the highest quality legal services with meticulous attention to detail
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-3">Client Focus</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Placing our clients' interests at the center of everything we do
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-3">Global Vision</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Understanding international markets and cross-border complexities
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-3">Integrity</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Maintaining the highest ethical standards in all our relationships
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Leadership */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">Our Leadership</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Senior leadership combining legal excellence, commercial judgment, and institutional governance
            </p>
          </div>

          <div className="space-y-16">
            {/* David Dolce */}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 lg:p-12">
                <div className="flex flex-col items-center text-center">
                  <div className="w-48 h-48 bg-gray-200 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                    <Users className="h-20 w-20 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-serif text-gray-900 mb-2">David Dolce</h3>
                  <p className="text-sm font-medium text-gray-600 mb-4">
                    Founder, Group Chief Executive Officer & Director
                  </p>
                </div>
                
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Client Value</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Senior commercial judgement across corporate, M&A, private capital and cross-border matters. David works with investors, founders and management teams on strategic transactions and complex business matters, with a focus on decision-making, negotiation and international coordination.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Profile</h4>
                    <p className="text-gray-700 leading-relaxed">
                      David leads Ashbourne's group strategy, international development and corporate growth. His background combines legal practice, corporate and commercial advisory, business leadership and cross-border professional activity across the UK, Italy and the Middle East.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Selected Professional Activity</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>10+ years of international legal and business experience</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>New York City Bar Association - International Law Committee (Class of 2026)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>New York City Bar Association - European Affairs Committee (Class of 2027)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>International arbitration / dispute-resolution activity and Swiss Arbitration Association (ASA) association</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>International investment and business-network activity through WBAF across England, Dubai and Italy</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Gillian Haselden */}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 lg:p-12">
                <div className="flex flex-col items-center text-center">
                  <div className="w-48 h-48 bg-gray-200 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                    <Users className="h-20 w-20 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-serif text-gray-900 mb-2">Gillian Haselden</h3>
                  <p className="text-sm font-medium text-gray-600 mb-4">
                    Managing Partner, Director & Compliance Officer for Legal Practice
                  </p>
                </div>
                
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Client Value</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Board-level legal judgement, governance discipline and risk management for complex organisations. Gillian helps boards and senior management navigate governance, legal risk, compliance and complex organisational decisions while maintaining clear professional standards and delivery oversight.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Profile</h4>
                    <p className="text-gray-700 leading-relaxed">
                      A Solicitor of England & Wales, Gillian brings senior legal, governance, risk and compliance leadership from major professional and financial-services organisations. At Ashbourne, she leads legal management, professional standards, supervision and regulatory oversight.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Selected Experience</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Former Deputy Chief Legal Officer at Ernst & Young (EY)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Chief Legal Officer of BT Pension Scheme Management Ltd (Brightwell), 2014-2025</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Executive Committee member and Board Director during her 11-year tenure at Brightwell</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>More than 20 years managing UK, European and global teams of approximately 10-125 people</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Held FCA SMF16 (Compliance Oversight) and SMF17 (MLRO) senior management functions</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Marco Maulucci */}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 lg:p-12">
                <div className="flex flex-col items-center text-center">
                  <div className="w-48 h-48 bg-gray-200 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                    <Users className="h-20 w-20 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-serif text-gray-900 mb-2">Marco Maulucci</h3>
                  <p className="text-sm font-medium text-gray-600 mb-4">
                    Co-Founder, Senior Partner, Director & Shareholder
                  </p>
                </div>
                
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Client Value</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Commercially grounded support for corporate, cross-border and strategic business matters. Marco supports companies and investors on corporate and commercial matters, helping coordinate legal work around expansion, strategic relationships and cross-border business objectives.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Profile</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Marco combines corporate and commercial legal work with senior client development, business origination and market development. His Ashbourne remit includes senior relationships, international partnerships and Europe / Switzerland strategic growth.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Professional Focus</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Corporate and commercial matters, including governance and strategic business issues</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>M&A and cross-border transactional support</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Corporate finance, internationalisation and strategic advisory</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Senior client relationships, business origination and professional partnerships</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gray-900 mr-2">•</span>
                        <span>Europe and Switzerland market development</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif mb-6">Our Approach</h2>
          <p className="text-xl text-gray-300 leading-relaxed mb-8">
            We combine sophisticated legal analysis with practical business judgment to deliver solutions that protect our clients' interests while enabling them to achieve their strategic objectives.
          </p>
          <p className="text-lg text-gray-400 leading-relaxed">
            Every engagement is led by a senior partner who takes personal responsibility for understanding your needs and delivering exceptional results. We believe in building long-term relationships based on trust, expertise, and consistent excellence.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
